import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { content } = useContent('footer');

  const t = content || {
    description: 'Créez des QR codes personnalisés et professionnels en quelques clics.',
    sections: { product: 'Produit', company: 'Entreprise', support: 'Support' },
    links: {
      product: [{ name: 'Fonctionnalités', href: '/product' }, { name: 'Tarifs', href: '/pricing' }],
      company: [{ name: 'À propos', href: 'https://www.scalesi.fr/' }, { name: 'Contact', href: '/?recipient=sales#contact' }],
      support: [{ name: 'Support', href: '/?recipient=support#contact' }, { name: 'Communauté', href: 'https://www.linkedin.com/company/quikr-it/?viewAsMember=true' }],
    },
    copyright: 'Tous droits réservés.',
    bottomLinks: { terms: 'Conditions Générales de Vente', cookies: 'Politique de cookies', rgpd: 'RGPD' },
  };

  const socialLinks = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/people/QuikRIt/61583716466030',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 12.073C22 6.505 17.523 2 12 2S2 6.505 2 12.073c0 5.022 3.657 9.195 8.438 9.927v-7.027H7.898v-2.9h2.54V9.845c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.47h-1.26c-1.243 0-1.63.774-1.63 1.562v1.875h2.773l-.443 2.9h-2.33V22C18.343 21.268 22 17.095 22 12.073z" />
        </svg>
      ),
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/quikr.it',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2c1.654 0 3 1.346 3 3s-1.346 3-3 3a3.004 3.004 0 01-3-3c0-1.654 1.346-3 3-3zm4.5-.75a1.25 1.25 0 11-.001 2.501A1.25 1.25 0 0116.5 8.25z" />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/109532374/admin/dashboard/',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  const renderLink = (link) => {
    const cls = 'text-gray-400 hover:text-white transition-colors duration-200 text-sm';
    if (link.href.startsWith('/')) {
      return <Link to={link.href} className={cls}>{link.name}</Link>;
    }
    return <a href={link.href} className={cls}>{link.name}</a>;
  };

  return (
    <footer className="bg-dark-900 border-t border-dark-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-40 mb-12">
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <Link to="/" className="inline-flex items-center space-x-2">
                <img
                  src="/images/logo.png"
                  alt="Quikr.it Logo"
                  className="w-40 h-20 object-contain"
                />
              </Link>
            </div>
            <p className="text-gray-400 text-sm mb-6 max-w-sm">
              {t.description}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-primary-500 transition-colors duration-200"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">{t.sections.product}</h3>
            <ul className="space-y-3">
              {t.links.product.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">{t.sections.company}</h3>
            <ul className="space-y-3">
              {t.links.company.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">{t.sections.support}</h3>
            <ul className="space-y-3">
              {t.links.support.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-dark-700 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-400 text-sm mb-4 md:mb-0">
            © {currentYear} Quikr. {t.copyright}
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">
              {t.bottomLinks.terms}
            </Link>
            <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors duration-200">
              {t.bottomLinks.cookies}
            </Link>
            <Link to="/rgpd" className="text-gray-400 hover:text-white transition-colors duration-200">
              {t.bottomLinks.rgpd}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
